import axios from 'axios';
import type { TicketListItem, TicketDetail, ReanalysisRequest, IngestResponse, HealthResponse, ProcessContext, PiiIgnoreEntry, ActiveDistribution, ScaffoldSegment, DraftKind, DeclineInstead, ActiveCounts, AnswerCapabilityEntry, AppConfig, ArchiveCounts, BrandConfig, ClaimVia, DetectedBrand, EditDraftSlotResult, EditSuggestionResult, GenerateTestEmailRequest, GeneratedEmail, PillFilterParams, RenderSecondaryDraftResult, ReplyPromptEntry, RoutingRule, RoutingRuleUpsert, RowPillPreferences, RowPillSurvey, SenderOrganisationMergeResult, SenderOrganisationRow, SenderOrganisationUpsert, SignalAtomEntry, TicketFacets, TicketStatistics, TriageActionResult, UiPreferences, UiWarps, UserEntry, UserRoles, VnbAct, VnbActUpsert, VocabularyCatalogResponse } from '../serverTypes';
import { getAuthHeader, onAuthRejected, beginInteractiveLogin } from '../auth/identity';
import { formatBackendError } from '../utils/formatError';
import { readCachedVocabConfig, writeConfigCache, type CachedVocabConfig } from './configCache';
// The one reading of a lost suggestion race — this client flags the envelope, every surface above
// reads that flag through the same predicate (services/staleSuggestion.ts).
import { isStaleSuggestionError } from './staleSuggestion';
import type { ActiveBucket, ArchiveBucket } from './lifecycleBuckets';
// Re-export so existing call sites can keep importing from services/api.
// Function bodies live in utils/formatError.ts so test files that auto-mock
// '../services/api' don't accidentally null them out.
export { formatBackendError, mapBackendError } from '../utils/formatError';

// <purpose>All API calls to the smarttix.core Function App (per-customer Bicep deployment).</purpose>
// <dependencies>getAuthHeader (auth/identity.ts) — Authorization: Bearer for cross-origin calls.</dependencies>
//
// Local dev: CRA proxy (setupProxy.js) routes /api → localhost:7071 (same-origin, no CORS).
// Production: REACT_APP_CORE_API_URL set at build time to the customer's FA (cross-origin).
const CORE_API_URL = process.env.REACT_APP_CORE_API_URL || '/api';

const coreHttp = axios.create({ baseURL: CORE_API_URL });
coreHttp.interceptors.request.use(async (config) => {
  const auth = await getAuthHeader();
  if (auth) config.headers[auth.name] = auth.value;
  return config;
});

// Why: backend sends camelCase via WorkerOptions.Serializer, but this safety net
// catches any residual PascalCase keys from WriteStringAsync paths.
const toCamelCase = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
// Why: skip keys that are domain identifiers (K0, K3A, reason codes like beschwerde) — only camelize PascalCase property names
const shouldCamelize = (s: string): boolean => s.length > 1 && s.charAt(0) >= 'A' && s.charAt(0) <= 'Z' && s.charAt(1) >= 'a' && s.charAt(1) <= 'z';
const camelizeKeys = (obj: unknown): unknown => {
  if (Array.isArray(obj)) return obj.map(camelizeKeys);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Blob)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [shouldCamelize(k) ? toCamelCase(k) : k, camelizeKeys(v)])
    );
  }
  return obj;
};
coreHttp.interceptors.response.use((response) => {
  if (response.config.responseType === 'blob') return response;
  if (response.data && typeof response.data === 'object') {
    response.data = camelizeKeys(response.data);
  }
  return response;
}, (error) => {
  // Why: a server-rejected token → backend 401 → empty dashboard instead of a login prompt.
  // Invalidate the token cache, then begin interactive login THROUGH the facade — it preserves
  // the current URL as the post-login return and owns the one door. Never a hard-coded href
  // here: a login link that bypasses the facade never feeds the MSAL cache.
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    onAuthRejected();
    void beginInteractiveLogin();
  }
  return Promise.reject(error);
});

// ─── Triage draft wire constants (Part C, 2026-09-07) ───
// This file is the adjudicated home of the triage pipeline's wire spellings (FE role-literal
// ratchet, services/__tests__/roleFlavouringScan.test.ts — two ledger rows for services/api.ts).
// Both are written out plainly, so each is greppable as the string that actually goes on the wire,
// and each is spelled ONCE — every call site imports the constant instead of re-typing the value,
// which is how a route or tag rename stays a one-line change.
// - TRIAGE_ACTIONS_ROUTE: the actions route every triage call builds on.
// - RENDER_SOURCE_TRIAGE_DRAFT: the render_source the triage composer stamps on a draft it
//   produced; services/draftClaims.ts re-exports it so draft surfaces read one vocabulary module
//   rather than the API client.
export const TRIAGE_ACTIONS_ROUTE = '/manage/wim/actions';
export const RENDER_SOURCE_TRIAGE_DRAFT = 'wim-draft-v1';

// ═══════════════════════════════════════════════════════════
// Phase 4B expand-window boundary coalesce (spec §6, task F1)
// ═══════════════════════════════════════════════════════════
// Expand-window tolerance, the FE's only knowledge of the legacy wire names: a pre-expansion BE
// serves only the old pair, an expanded BE serves both — coalesce once here at the parse
// boundary, deleted at the contraction (see smarttix-core.md § Contraction discipline). No
// component below this line may mention the legacy names.

const normalizeTicket = <T extends {
  primaryAxis?: string; category?: string;
  classifierConfidence?: number; categoryConfidence?: number;
  secondaryAxis?: string[]; reasonCodes?: string[];
}>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
  classifierConfidence: raw.classifierConfidence ?? raw.categoryConfidence,
  secondaryAxis: raw.secondaryAxis ?? raw.reasonCodes,
});

const normalizeSuggestion = <T extends {
  primaryAxis?: string; category?: string;
  suggestionConfidence?: number; confidence?: number;
}>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
  suggestionConfidence: raw.suggestionConfidence ?? raw.confidence,
} as T);

const normalizeRejectionFlowItem = <T extends {
  fromPrimaryAxis?: string; fromCategory?: string;
  toPrimaryAxis?: string; toCategory?: string;
}>(raw: T): T => ({
  ...raw,
  fromPrimaryAxis: raw.fromPrimaryAxis ?? raw.fromCategory,
  toPrimaryAxis: raw.toPrimaryAxis ?? raw.toCategory,
} as T);

const normalizePrimaryAxisVolumeBin = <T extends { primaryAxis?: string; category?: string }>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
});

// Statistics carries the widest fan-out of renamed fields (spec §6 E3 list) — one coalesce home
// covering every sub-object the FE consumes, applied once at getStatistics' parse boundary.
const normalizeStatistics = (raw: any): TicketStatistics => ({
  ...raw,
  perPrimaryAxisCount: raw.perPrimaryAxisCount ?? raw.categories,
  perSecondaryAxisCount: raw.perSecondaryAxisCount ?? raw.reasonCodes,
  rejectionFlow: raw.rejectionFlow?.map(normalizeRejectionFlowItem),
  perPrimaryAxisVolumeByPeriod: (raw.perPrimaryAxisVolumeByPeriod ?? raw.categoryVolumeByPeriod)?.map(normalizePrimaryAxisVolumeBin),
  perPrimaryAxisA0Rate: raw.perPrimaryAxisA0Rate ?? raw.categoryA0Rate,
  perPrimaryAxisA2Rate: raw.perPrimaryAxisA2Rate ?? raw.categoryA2Rate,
  perPrimaryAxisOffen: raw.perPrimaryAxisOffen ?? raw.categoryOffen,
  perPrimaryAxisErfolgreich: raw.perPrimaryAxisErfolgreich ?? raw.categoryErfolgreich,
  perPrimaryAxisRetryBuckets: raw.perPrimaryAxisRetryBuckets ?? raw.categoryRetryDistribution,
  perPrimaryAxisBaselines: raw.perPrimaryAxisBaselines ?? raw.categoryBaselines,
  avgClassifierConfidence: raw.avgClassifierConfidence ?? raw.avgCategoryConfidence,
  highClassifierConfidenceRate: raw.highClassifierConfidenceRate ?? raw.highConfidenceRate,
  archivedUnclassifiedCount: raw.archivedUnclassifiedCount ?? raw.archivedUnklarCount,
  totalUnclassifiedCount: raw.totalUnclassifiedCount ?? raw.totalUnklarCount,
});

// The query-param spelling of PillFilterParams: comma-joined lists, omitted when absent or empty (an
// empty param would read as a filter on nothing) — the same posture as sparte/senderKind beside it.
function appendPillFilterParams(params: URLSearchParams, p: PillFilterParams): void {
  if (p.flag && p.flag.length > 0) params.append('flag', p.flag.join(','));
  if (p.searchPrimary && p.searchPrimary.length > 0) params.append('searchPrimary', p.searchPrimary.join(','));
  if (p.searchSecondary && p.searchSecondary.length > 0) params.append('searchSecondary', p.searchSecondary.join(','));
  if (p.hasForm === true) params.append('hasForm', 'true');
}

// Why: server-default fallback matches migration 037 defaults so a /config 4xx
// (e.g. ancient backend without uiWarps) doesn't flip historical warps off mid-deploy.
const DEFAULT_UI_WARPS: UiWarps = {
  processToAntworten: true,
  testgenProcessToAntworten: true,
  testgenUploadToUploads: true,
};

// THE fetch seam (Task C, layer b): every successful resolution writes its vocabulary/role/
// row-layout slice into the sessionStorage cache (configCache.ts) — App.tsx reads that cache
// through once at boot for an instant first paint. This function ALWAYS still hits the network on
// every call, cache or no cache: EinstellungenSeite's explicit refreshConfig() after a settings
// save (and Task B's formExtractionEnabled security gate) depend on that never being skipped — the
// cache only ever shortens the pre-paint window, never substitutes for the authoritative fetch. A
// FAILED fetch (catch path below) deliberately does NOT touch the cache: a transient outage must
// never overwrite a good cached entry with nothing.
export const getConfig = async (): Promise<AppConfig> => {
  try {
    const response = await coreHttp.get<AppConfig>('/config');
    const normalized: AppConfig = {
      ...response.data,
      uiWarps: { ...DEFAULT_UI_WARPS, ...(response.data.uiWarps ?? {}) },
      autoArchiveDays: response.data.autoArchiveDays ?? 0,
      classificationSchema: response.data.classificationSchema ?? null,
      // Absent on an older backend → null → the FE behaves exactly as it did before this field existed.
      classificationVocabulary: response.data.classificationVocabulary ?? null,
      uiRowLayout: response.data.uiRowLayout ?? undefined, // null/unset → App applies the role-aware default
      simulateSystemData: response.data.simulateSystemData ?? false,
      // ⚠ Absent ⇒ HIDDEN, in both the success and the catch path below. A backend that does not
      // know the field yet, and a /config that failed outright, mean the same thing here: nobody
      // asked for the row. Defaulting it visible would ship the state the operator's E2E rejected.
      uiShowSourceChannel: response.data.uiShowSourceChannel ?? false,
      // Same fail-closed shape: absent means the capability is not owned, never a guessed grant.
      formExtractionEnabled: response.data.formExtractionEnabled ?? false,
      // ⚠ Absent ⇒ TRUE — inverted from every other flag in this block on purpose. This is a
      // display preference, not a capability: nobody having set it means "keep counting inline
      // images", which is the behaviour every already-live tenant has today. A `false` here would
      // silently change what clerks see, so it takes the opposite fallback direction from
      // uiShowSourceChannel/formExtractionEnabled above.
      uiAttachmentsCountInline: response.data.uiAttachmentsCountInline ?? true,
      // Absent ⇒ null — the code default applies (see the field's own comment).
      uiRowPillsHidden: response.data.uiRowPillsHidden ?? null,
      // ⚠ Absent ⇒ [] — never undefined. An older backend serving no field reads exactly like a
      // tenant whose clerks can approve only one kind (see the field's own comment above).
      decisionKinds: response.data.decisionKinds ?? [],
      // ⚠ Absent ⇒ {} — never undefined, same fail-open direction as decisionKinds above. A role
      // with no declared handling and a backend that predates the field read identically.
      decisionKindHandling: response.data.decisionKindHandling ?? {},
      // ⚠ Absent ⇒ {} — nothing implied, every flag drawn (see the field's own comment).
      flagLandings: response.data.flagLandings ?? {},
    };
    writeConfigCache({
      role: normalized.role,
      classificationSchema: normalized.classificationSchema ?? null,
      classificationVocabulary: normalized.classificationVocabulary ?? null,
      uiRowLayout: normalized.uiRowLayout,
      uiAttachmentsCountInline: normalized.uiAttachmentsCountInline,
      decisionKinds: normalized.decisionKinds,
      decisionKindHandling: normalized.decisionKindHandling,
      flagLandings: normalized.flagLandings,
    });
    return normalized;
  } catch {
    // Why role: null (not a fabricated 'gmsb'): a failed /config means the FE genuinely does not
    // know the tenant's role. Silently assuming gmsb dressed a backend outage as a working gmsb
    // tenant — the caller (App.tsx) maps this to the neutral/warning UI instead.
    // uiAttachmentsCountInline: true even here — a backend outage must not look like a tenant
    // asking to stop counting inline images; see the success-path comments above for why this
    // flag inverts
    // direction from their fail-closed neighbours in this same object.
    return { chatbotEnabled: false, chatbotBaseUrl: '', role: null, uiWarps: DEFAULT_UI_WARPS, autoArchiveDays: 0, classificationSchema: null, classificationVocabulary: null, uiRowLayout: undefined, simulateSystemData: false, uiShowSourceChannel: false, formExtractionEnabled: false, uiAttachmentsCountInline: true, uiRowPillsHidden: null, decisionKinds: [], decisionKindHandling: {}, flagLandings: {} };
  }
};

// Read-through for App.tsx's instant first paint (Task C) — see configCache.ts for the full
// contract (self-consistency check, session-scoping rationale, why capability flags never ride
// along). Never touches the network; a miss (first-ever boot this tab) simply returns null and
// the caller keeps its existing neutral defaults until getConfig() resolves.
export const getCachedVocabConfig = (): CachedVocabConfig | null => readCachedVocabConfig();

// Fire-and-forget /config pre-warm (Task C, layer c) — App.tsx fires this once when the Anmeldung
// (login) screen renders, before the operator has necessarily completed sign-in. Deliberately
// bypasses coreHttp: its response interceptor above begins interactive login on ANY 401, and
// firing that from the login screen itself — before the operator has clicked anything — would be
// an unsolicited navigation on the one page whose entire job is to wait for their click. A bare
// fetch cannot trigger that interceptor. The expected pre-login 401 is handled by `r.ok` below,
// NOT by the trailing .catch(): fetch() only rejects on a genuine network-level failure (offline,
// DNS, CORS) — an HTTP error STATUS like 401 is still a resolved response, just with `ok: false`,
// so it flows into the `r.ok ? r.json() : null` branch and resolves to `null`, which the `if
// (data && …)` guard below then skips silently. .catch() exists only for that separate,
// network-level failure class.
// Returns void, never a promise the caller is expected to await or chain — this exists purely to
// warm the backend's cold start / DB connection pool before the REAL getConfig() call fires
// post-login. Feeding the vocabulary cache from a genuinely-successful response (e.g. an
// already-valid SWA session cookie riding along on this tab) is a bonus, not the point — it
// deliberately skips getConfig()'s own normalization (uiWarps merge etc.), since an
// under-normalized cache entry is corrected by the very next real getConfig() call regardless, and
// only the vocabulary-relevant slice is ever written (never formExtractionEnabled/uiWarps/etc. —
// see configCache.ts's own constraint against caching capability flags from an unauthenticated call).
export const prewarmConfig = (): void => {
  fetch(`${CORE_API_URL}/config`, { credentials: 'include' })
    .then(r => (r.ok ? r.json() : null))
    .then((data: Partial<AppConfig> | null) => {
      if (data && typeof data === 'object' && 'role' in data) {
        writeConfigCache({
          role: data.role ?? null,
          classificationSchema: data.classificationSchema ?? null,
          classificationVocabulary: data.classificationVocabulary ?? null,
          uiRowLayout: data.uiRowLayout,
        });
      }
    })
    .catch(() => { /* silent — warm-up only, see purpose above */ });
};

// GET /brand — the customer's stored brand row (Chatbot-Testseite preview). Null on any error.
export const getBrand = async (): Promise<BrandConfig | null> => {
  try {
    const response = await coreHttp.get<BrandConfig>('/brand');
    return response.data;
  } catch (err) {
    console.error('[API] Failed to get brand', err);
    return null;
  }
};

// GET /brand/detected — the last auto-detected palette for the Chatbot-Testseite. Null on 204
// (zero-visit), empty body, or any error. The auto card renders it inline when present.
export const getBrandDetected = async (): Promise<DetectedBrand | null> => {
  try {
    const response = await coreHttp.get<DetectedBrand | ''>('/brand/detected');
    // 204 No Content → axios yields status 204 with '' data; a real row → the object.
    if (response.status === 204 || !response.data) return null;
    return response.data as DetectedBrand;
  } catch (err) {
    console.error('[API] Failed to get detected brand', err);
    return null;
  }
};

export const getUserRoles = async (email?: string): Promise<UserRoles | null> => {
  try {
    const url = email ? `/roles?email=${encodeURIComponent(email)}` : `/roles`;
    console.log(`[API] Fetching user roles for: ${email || 'current user'} from ${url}`);
    const response = await coreHttp.get<UserRoles>(url);
    console.log('[API] User roles received:', response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(`[API] Failed to get user roles: ${err.response?.status} ${err.response?.statusText}`, err.response?.data);
    } else {
      console.error('[API] Failed to get user roles (non-axios error)', err);
    }
    return null;
  }
};

function serializeDateRanges(ranges: Array<{start: Date; end: Date}>): string {
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return ranges.map(r => `dateRanges=${fmt(r.start)}..${fmt(r.end)}`).join('&');
}

export const getStatistics = async (
  dateRanges: Array<{start: Date; end: Date}>,
  dateField?: string,
  aggregation?: string,
): Promise<TicketStatistics | null> => {
  try {
    const parts: string[] = [];
    if (dateRanges.length > 0) parts.push(serializeDateRanges(dateRanges));
    if (dateField) parts.push(`dateField=${encodeURIComponent(dateField)}`);
    if (aggregation && aggregation !== 'daily') parts.push(`aggregation=${encodeURIComponent(aggregation)}`);
    const url = parts.length > 0 ? `/statistics?${parts.join('&')}` : '/statistics';
    const response = await coreHttp.get<TicketStatistics>(url);
    return normalizeStatistics(response.data);
  } catch (err) {
    console.error('Failed to get statistics', err);
    return null;
  }
};

export const getActiveDistribution = async (): Promise<ActiveDistribution | null> => {
  try {
    const response = await coreHttp.get<ActiveDistribution>(`/tickets/active/distribution`);
    return response.data;
  } catch (err) {
    console.error('Failed to get active distribution', err);
    return null;
  }
};

// Sender-identity registry (P2, 2026-09-13), Task R2 — ONE endpoint, `/tickets/facets`, serving
// every bucket both the active AND archive lists serve (controller ruling, spec §3.5, fix round 1:
// NOT `/tickets/active/facets` — that path was this file's own guess, wrong against the actual
// contract). `bucket` is how the ONE endpoint answers for whichever list is asking, which is also
// why it stays a plain string here rather than the active-only union.
// The SAME query params as smarttix.getActivePage/getArchivedPage (see those for the
// param-building precedent this mirrors line for line: dual-sent category/primaryAxis +
// reasonCodes/secondaryAxis, comma-joined sparte/senderKind/senderOrganisationId,
// omitted-when-absent-or-empty). A facet answers "what would every OTHER value of this filter
// show", which only makes sense against the SAME filter set the list itself is reading — a facets
// call with different params from its list call would answer a question the clerk never asked.
// Returns null on any failure — the caller's job is to keep the LAST successful read rather than
// clear the panel to empty mid-fetch (UI Layout Stability; see AntwortenSeite/ArchivSeite).
export const getTicketFacets = async (p: PillFilterParams & {
  bucket?: string;
  search?: string;
  primaryAxis?: string;
  secondaryAxis?: string[];
  sparte?: string[];
  senderKind?: string[];
  senderOrganisationId?: string[];
  from?: string | null;
  to?: string | null;
  uploadedBy?: string;
}): Promise<TicketFacets | null> => {
  try {
    const params = new URLSearchParams();
    if (p.bucket) params.append('bucket', p.bucket);
    if (p.search) params.append('search', p.search);
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.sparte && p.sparte.length > 0) params.append('sparte', p.sparte.join(','));
    if (p.senderKind && p.senderKind.length > 0) params.append('senderKind', p.senderKind.join(','));
    if (p.senderOrganisationId && p.senderOrganisationId.length > 0) params.append('senderOrganisationId', p.senderOrganisationId.join(','));
    appendPillFilterParams(params, p);
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    const response = await coreHttp.get<TicketFacets>(`/tickets/facets?${params.toString()}`);
    return response.data;
  } catch (err) {
    console.error('Failed to get ticket facets', err);
    return null;
  }
};

export const getMonthlyTicketCounts = async (dateField: string): Promise<Record<string, number>> => {
  try {
    const url = `/statistics/monthly-counts?dateField=${encodeURIComponent(dateField)}`;
    const response = await coreHttp.get<Record<string, number>>(url);
    return response.data;
  } catch (err) {
    console.error('Failed to fetch monthly counts', err);
    return {};
  }
};

/** Trigger browser file download from a Blob. */
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const exportStatistics = async (
  dateRanges: Array<{start: Date; end: Date}>,
  dateField: string,
  format: 'xlsx' | 'pdf',
  aggregation?: string,
  // WCAG 2.2.1 / DGUV Ch.9 — cancellable long op. BE may still finish rendering
  // the export after cancel; the FE just discards the response. Acceptable trade-off
  // (no client-state corruption; BE work is read-only).
  options?: { signal?: AbortSignal },
): Promise<void> => {
  const parts: string[] = [`dateField=${encodeURIComponent(dateField)}`, `format=${encodeURIComponent(format)}`];
  if (dateRanges.length > 0) parts.push(serializeDateRanges(dateRanges));
  if (aggregation && aggregation !== 'daily') parts.push(`aggregation=${encodeURIComponent(aggregation)}`);
  const response = await coreHttp.get(`/statistics/export?${parts.join('&')}`, {
    responseType: 'blob',
    signal: options?.signal,
  });
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const firstStart = dateRanges[0]?.start;
  const lastEnd = dateRanges[dateRanges.length - 1]?.end;
  const fromStr = firstStart ? firstStart.toISOString().slice(0, 10).replace(/-/g, '') : 'unknown';
  const toStr = lastEnd ? lastEnd.toISOString().slice(0, 10).replace(/-/g, '') : 'unknown';
  // The filename's single home is the server's Content-Disposition (deterministic per tenant —
  // the client's downstream tooling refreshes from a fixed path). The date-range name survives
  // only as the fallback for a backend that does not expose the header cross-origin yet.
  const fallback = `statistik-${fromStr}-${toStr}.${ext}`;
  const filename = filenameFromContentDisposition(response.headers['content-disposition'], fallback);
  downloadBlob(new Blob([response.data]), filename);
};

// Parses `attachment; filename="..."` (quoted or bare). Exported for its test; consumed only by
// exportStatistics above. Never throws — an unparseable header is the fallback's case.
export const filenameFromContentDisposition = (header: string | null | undefined, fallback: string): string => {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
};

// ═══════════════════════════════════════════════════════════
// Smarttix Pipeline API (cross-origin, auth via interceptor)
// ═══════════════════════════════════════════════════════════

// <purpose>The download's visible filename.</purpose>
// <constraints>Mirrors FormBatchExcelBuilder.FileNameFor on the server. NOT "aufbauliste.xlsx" — that read
// as the customer's own official sheet, which is the opposite of what this file is, and repeat downloads
// collided into "(1)". Row count so two downloads are distinguishable at a glance; timestamp
// largest-unit-first so a folder sorts chronologically on name alone. Local time on purpose: the clerk
// reads it against their own clock, not the server's.</constraints>
const notizblattFileName = (rowCount: number): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
  return `Notizblatt_${rowCount}-Zeilen_${stamp}.xlsx`;
};

export const smarttix = {
  checkHealth: async (): Promise<HealthResponse> => {
    const response = await coreHttp.get<HealthResponse>('/tickets/health');
    return response.data;
  },

  ingestTicket: async (file: File, options?: { parseOnly?: boolean; context?: string; signal?: AbortSignal }): Promise<IngestResponse> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (options?.context) formData.append('context', options.context);
    // mig 072 — this client DECLARES its own transport route rather than letting the server infer one.
    // ⚠ It is a declaration, not proof: the server normalizes it against a closed vocabulary and stores
    // NULL for anything it does not recognise, and nothing downstream may treat it as a security fact.
    // The other route ('forward', a future Outlook POST flow) declares itself the same way when it is
    // built; until then those tickets are honestly unknown rather than defaulted into a claim.
    formData.append('channel', 'upload');
    const params = options?.parseOnly ? '?parseOnly=true' : '';
    const response = await coreHttp.post<IngestResponse>(
      `/tickets/ingest${params}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // WCAG 2.2.1 / DGUV Ch.9 — long ops must be cancellable. axios respects AbortSignal.
        signal: options?.signal,
      }
    );
    return response.data;
  },

  processTicket: async (uuid: string, processContext?: ProcessContext): Promise<{ success: boolean; error?: string }> => {
    try {
      const body: Record<string, unknown> = processContext && Object.keys(processContext).length > 0 ? { ...processContext } : {};
      // Phase 4B expand window: dual-send both the old and new axis-vocabulary body key with the
      // SAME value — an old (pre-expansion) BE reads forceCategory/forceReasonCodes, an expanded
      // one can assert the two agree. Canonical source is the new-named field (ProcessContext);
      // the old key is computed FROM it, never the other way — same direction as the query-param senders above.
      if (processContext?.forcePrimaryAxis !== undefined) body.forceCategory = processContext.forcePrimaryAxis;
      if (processContext?.forceSecondaryAxis !== undefined) body.forceReasonCodes = processContext.forceSecondaryAxis;
      await coreHttp.post(`/tickets/${uuid}/process`, body);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Verarbeiten') };
    }
  },

  deleteTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Löschen') };
    }
  },

  bulkDeleteTickets: async () => {
    try {
      const response = await coreHttp.delete<{ deletedAt: string }>(`/tickets`);
      return response.data;
    } catch (error: any) {
      return { success: false as const, error: formatBackendError(error, 'Massenlöschen') };
    }
  },

  undoBulkDelete: async (deletedAt: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post('/tickets/undo-bulk-delete', { deletedAt });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig machen') };
    }
  },

  getArchivedPage: async (
    p: PillFilterParams & {
      bucket: ArchiveBucket;
      search?: string;
      primaryAxis?: string;
      secondaryAxis?: string[];
      from?: string | null;
      to?: string | null;
      uploadedBy?: string;
      // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13), comma-joined below.
      // Spelled as the CAMELCASE wire field, not as the served snake_case attribute name — the
      // translation happens once, in services/ticketAttributes.
      sparte?: string[];
      senderKind?: string[];
      // Sender-identity registry (P2, 2026-09-13), Task R2 — the organisation filter's own list
      // param, comma-joined like sparte/senderKind and NOT translated through ticketAttributes:
      // the matched organisation is a registry row, not a classified-alongside attribute.
      senderOrganisationId?: string[];
      cursor?: string | null;
      limit?: number;
    }
  ): Promise<{ rows: TicketListItem[]; nextCursor: string | null; counts: ArchiveCounts | null }> => {
    const params = new URLSearchParams({ bucket: p.bucket });
    if (p.search) params.append('search', p.search);
    // Phase 4B expand window: dual-send both the old and new axis-vocabulary param name with the
    // SAME value — an old (pre-expansion) BE reads 'category'/'reasonCodes', an expanded one can
    // assert the two agree (see normalizeTicket above for the read-side coalesce).
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    // Comma-joined like reasonCodes/secondaryAxis above, and deliberately NOT dual-sent: both names
    // are new on both sides of the wire, so there is no older spelling to keep alive. Omitted when
    // empty — an empty param would read as a filter on nothing.
    if (p.sparte && p.sparte.length > 0) params.append('sparte', p.sparte.join(','));
    if (p.senderKind && p.senderKind.length > 0) params.append('senderKind', p.senderKind.join(','));
    if (p.senderOrganisationId && p.senderOrganisationId.length > 0) params.append('senderOrganisationId', p.senderOrganisationId.join(','));
    appendPillFilterParams(params, p);
    if (p.cursor) params.append('cursor', p.cursor);
    if (p.limit) params.append('limit', p.limit.toString());
    const response = await coreHttp.get<{ rows: TicketListItem[]; nextCursor: string | null; counts: ArchiveCounts | null }>(`/tickets/archived?${params.toString()}`);
    return { ...response.data, rows: response.data.rows.map(normalizeTicket) };
  },

  getActivePage: async (
    p: PillFilterParams & {
      bucket: ActiveBucket;
      sort: 'uploaded' | 'received' | 'updated' | 'rejections' | 'zone';
      dir?: 'desc' | 'asc';
      search?: string;
      primaryAxis?: string;
      secondaryAxis?: string[];
      from?: string | null;
      to?: string | null;
      uploadedBy?: string;
      // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13), comma-joined below.
      // Spelled as the CAMELCASE wire field, not as the served snake_case attribute name — the
      // translation happens once, in services/ticketAttributes.
      sparte?: string[];
      senderKind?: string[];
      // Sender-identity registry (P2, 2026-09-13), Task R2 — see getArchivedPage's own field above.
      senderOrganisationId?: string[];
      cursor?: string | null;
      limit?: number;
    }
  ): Promise<{ rows: TicketListItem[]; nextCursor: string | null; counts: ActiveCounts | null }> => {
    const params = new URLSearchParams({ bucket: p.bucket, sort: p.sort });
    if (p.dir) params.append('dir', p.dir);
    if (p.search) params.append('search', p.search);
    // Phase 4B expand window: dual-send both the old and new axis-vocabulary param name with the
    // SAME value — an old (pre-expansion) BE reads 'category'/'reasonCodes', an expanded one can
    // assert the two agree (see normalizeTicket above for the read-side coalesce).
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    // Comma-joined like reasonCodes/secondaryAxis above, and deliberately NOT dual-sent: both names
    // are new on both sides of the wire, so there is no older spelling to keep alive. Omitted when
    // empty — an empty param would read as a filter on nothing.
    if (p.sparte && p.sparte.length > 0) params.append('sparte', p.sparte.join(','));
    if (p.senderKind && p.senderKind.length > 0) params.append('senderKind', p.senderKind.join(','));
    if (p.senderOrganisationId && p.senderOrganisationId.length > 0) params.append('senderOrganisationId', p.senderOrganisationId.join(','));
    appendPillFilterParams(params, p);
    if (p.cursor) params.append('cursor', p.cursor);
    if (p.limit) params.append('limit', p.limit.toString());
    const response = await coreHttp.get<{ rows: TicketListItem[]; nextCursor: string | null; counts: ActiveCounts | null }>(`/tickets/active?${params.toString()}`);
    return { ...response.data, rows: response.data.rows.map(normalizeTicket) };
  },

  getTicket: async (uuid: string): Promise<TicketDetail | null> => {
    try {
      const response = await coreHttp.get<any>(`/tickets/${uuid}`);
      const detail = normalizeTicket(response.data);
      return { ...detail, suggestion: detail.suggestion ? normalizeSuggestion(detail.suggestion) : detail.suggestion };
    } catch {
      return null;
    }
  },

  // Audit one PII pill reveal click. BE writes a smarttix_log row with type+ordinal — never the value.
  // Fire-and-forget from the FE perspective; failures are swallowed (audit is defense-in-depth, not a hard gate).
  logPiiReveal: async (uuid: string, fieldType: string, index: number): Promise<void> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/pii-reveal-audit`, { fieldType, index });
    } catch { /* silent — see audit-as-defense-in-depth in pii-reveal-defensibility.md */ }
  },

  approveTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Genehmigen') };
    }
  },

  // The undo of an approval (Genehmigen settles the row, 2026-10-23): DELETE on the same resource,
  // the twin of withdrawDecline below — the row returns to Offen, approved_at and the approve claims
  // are taken back (a copy that was upgraded stays a copy). Never restricted server-side.
  withdrawApproval: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}/approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig') };
    }
  },

  // ─── „Nicht hilfreich" (bottom-bar decision, 2026-10-16) ───
  // The third act beside approve and request-reanalysis, in the same shape: one press = the verdict
  // (the row settles into Entschieden as declined — decided, not filed; Genehmigen settles the row,
  // 2026-10-23). The receiver only PRESSES — it carries what the clerk chose in the chooser and
  // nothing else; no act logic lives here. `instead` null = „Ohne Angabe ablegen". The undo is DELETE
  // on the same resource, exactly like the copy-approve undo.
  declineTicket: async (uuid: string, instead: DeclineInstead | null): Promise<{ success: boolean; error?: string }> => {
    try {
      const body = instead === null
        ? {}
        : instead.kind === 'primary_axis'
          ? { insteadKind: 'primary_axis', insteadValue: instead.value }
          : { insteadKind: instead.kind };
      await coreHttp.post(`/tickets/${uuid}/decline`, body);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Nicht hilfreich') };
    }
  },

  withdrawDecline: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}/decline`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig') };
    }
  },

  // ─── Draft claim family (Part C, 2026-09-07) ───
  // A CLAIM records which artifact the clerk actually used. Two server answers here are NOT
  // errors and must never render as one: a refusal arrives as HTTP 200 with executed:false and
  // the server's own reason, and a stale suggestion version is that same 200 with a resultState
  // the caller has to tell apart — its remedy is "reload", not "try again".
  claimDraft: async (
    uuid: string, suggestionVersion: number, kind: DraftKind, via: ClaimVia = 'approve',
  ): Promise<TriageActionResult> => {
    try {
      const res = await coreHttp.post<{ executed: boolean; resultState: string; rejectReason?: string }>(
        TRIAGE_ACTIONS_ROUTE,
        { suggestionUuid: uuid, suggestionVersion, action: `claim_${kind}`, via });
      if (res.data.executed) return { success: true, resultState: res.data.resultState };
      return {
        success: false,
        resultState: res.data.resultState,
        error: res.data.rejectReason,
        ...(res.data.resultState === 'stale_rejected' ? { stale: true } : {}),
      };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Triage-Aktion') };
    }
  },

  // Releases the claim of ONE kind — the other kind's claim on the same row is untouched.
  // Reads the SAME outcome envelope claimDraft reads (2026-09-07), because core answers HTTP 200
  // for every undo outcome and says in the body whether it actually EXECUTED: not_undoable (the
  // claim is no longer releasable), stale_rejected (the row moved on under the clerk),
  // rejected_ineligible. Taking the 200 itself for success — which this did — told the host every
  // release had happened, so it cleared the row's copy marker and said nothing while the claim
  // demonstrably still stood. A missing/blank body is therefore NOT success either: an answer that
  // does not say it executed has not said the claim is gone.
  unclaimDraft: async (uuid: string, kind: DraftKind): Promise<TriageActionResult> => {
    try {
      const res = await coreHttp.delete<{ executed: boolean; resultState: string; rejectReason?: string }>(
        `${TRIAGE_ACTIONS_ROUTE}/${uuid}/claims/${kind}`);
      if (res.data?.executed) return { success: true, resultState: res.data.resultState };
      return {
        success: false,
        resultState: res.data?.resultState,
        error: res.data?.rejectReason,
        ...(res.data?.resultState === 'stale_rejected' ? { stale: true } : {}),
      };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Zurücknehmen') };
    }
  },

  // Composes the row's OTHER draft on demand. suggestionId is the draft's own identity, so the
  // server composes against the exact row the clerk is looking at rather than whichever is newest
  // (a 422 says the composer had nothing to produce).
  // The stale reading goes through isStaleSuggestionError, NOT through the status alone
  // (2026-09-07): this endpoint answers 409 for more than one conflict — core's „keine weiteren
  // Entwürfe" is one — and only the one carrying its stale SENTENCE means "reload". Flagging every
  // 409 stale made the host silently reload and show the reload toast for a refusal it should have
  // stated plainly.
  renderSecondaryDraft: async (
    // Every kind that HAS an artifact, the receipt included (2026-09-08) — it is composed from the
    // tenant's own receipt template through this same endpoint — and the Auskunft letter since
    // 2026-09-16 (P3 v2), which core's own kind parse takes. The union is spelled out rather
    // than imported from services/draftClaims, which imports FROM this file.
    uuid: string, suggestionId: number, kind: 'reply' | 'forward' | 'ack' | 'internal',
  ): Promise<RenderSecondaryDraftResult> => {
    try {
      const res = await coreHttp.post<{ text: string; renderSource?: string; scaffoldJson?: string | null }>(
        `/manage/suggestions/${uuid}/render-secondary`, { suggestionId, kind });
      // The answer is handed back WHOLE (2026-09-17), not just its text: the caller reveals the
      // composed card from it. Keys the server did not send are omitted rather than nulled, so a
      // reveal built on this can tell "no letter" from "a letter this core did not describe".
      return {
        success: true,
        text: res.data.text,
        ...(typeof res.data.renderSource === 'string' ? { renderSource: res.data.renderSource } : {}),
        ...(res.data.scaffoldJson !== undefined ? { scaffoldJson: res.data.scaffoldJson } : {}),
      };
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      return {
        success: false,
        error: formatBackendError(error, 'Entwurf erzeugen'),
        ...(isStaleSuggestionError(error) ? { stale: true } : {}),
        ...(status === 422 ? { refused: true } : {}),
      };
    }
  },

  // Rewrites ONE composed draft slot (Part C wave 2, 2026-09-09). The KIND names the slot in the
  // ROUTE — it is what is being written, not a parameter of the write — and suggestionId rides the
  // body as the row's identity, the same discipline renderSecondaryDraft and the segments PATCH
  // use: the server writes the exact row the clerk was reading, never whichever is newest.
  // Success is 204 No Content. There is nothing to read back and the caller REFETCHES for the new
  // text and the new stamp, so this client never invents a second source for either.
  // The errors are text/plain (core's AuthHelpers.WriteErrorAsync), not the JSON envelope the
  // segments PATCH answers with — which is exactly why the stale reading goes through
  // isStaleSuggestionError, matching the BODY SENTENCE. Three different 409s reach here and only
  // the moved-on one means "reload"; „Bereits genehmigt." (the slot is claimed — copy claims
  // included, since a copy is the indirect approval) and „Entwurf wurde noch nicht erzeugt" are
  // refusals the surface STATES. Flagging the status alone would silently reload for both and
  // swallow the sentence the clerk has to act on.
  editDraftSlot: async (
    uuid: string,
    // The kinds core's EditDraftSlot will TAKE (its own `EditableKinds`, the Auskunft letter
    // included since 2026-09-16, plus the receipt — which it refuses with a sentence of its own
    // rather than as an unknown kind).
    // `scaffoldSegments` (Task 6b) carries a SLOT FILL: the letter's segments in the served shape
    // and the served order, which the server checks against the row before rewriting the scaffold
    // and its counts (400 `segments_mismatch` otherwise). Absent for a free-text rewrite, where
    // there is no scaffold to rewrite — the key is omitted rather than sent null, so a plain
    // rewrite's request body is byte-identical to the one this client has always sent.
    body: {
      suggestionId: number;
      kind: 'reply' | 'forward' | 'ack' | 'internal';
      text: string;
      scaffoldSegments?: ScaffoldSegment[];
    },
  ): Promise<EditDraftSlotResult> => {
    try {
      await coreHttp.patch(`/manage/suggestions/${uuid}/drafts/${body.kind}`, {
        suggestionId: body.suggestionId,
        text: body.text,
        ...(body.scaffoldSegments ? { scaffoldSegments: body.scaffoldSegments } : {}),
      });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: formatBackendError(error, 'Speichern des Entwurfs'),
        ...(isStaleSuggestionError(error) ? { stale: true } : {}),
      };
    }
  },

  // Without responseType 'blob' axios coerces the mail bytes to a string and the saved .eml is
  // corrupt in a way that only surfaces when a mail client opens it — no error, no failed request.
  // The kinds core's .eml endpoint will serve — the three letters plus the receipt.
  downloadTriageDraft: async (
    uuid: string, type: 'reply' | 'forward' | 'ack' | 'internal',
  ): Promise<void> => {
    const response = await coreHttp.get(`${TRIAGE_ACTIONS_ROUTE}/${uuid}/draft`, {
      params: { type },
      responseType: 'blob',
    });
    downloadBlob(new Blob([response.data]), `${type}-entwurf-${uuid}.eml`);
  },

  // Copy = indirect approval (#copy-approval). Sets copy_approved_at WITHOUT
  // advancing status — the ticket stays in Offen. undoCopyApprove is the
  // write-then-revert undo (DELETE clears copy_approved_at).
  copyApproveTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/copy-approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Kopieren') };
    }
  },

  // Per-ticket counterpart of downloadFormRowsXlsx. The row is assembled SERVER-side and fetched on
  // click rather than shipped on the detail DTO — the DTO carries only hasFormExtraction, so no form
  // field values reach the browser until a clerk actually asks for the row.
  // The server may REFUSE. The clipboard carries values only, while the workbook carries the notes that
  // say what was read and what disagreed — and the destination sheet is terminal, so a questionable cell
  // copied by clipboard arrives with no way to check it. A refusal is a 200 with a reason, not an error,
  // and the caller renders that reason.
  copyFormRow: async (uuid: string): Promise<{ row?: string; blocked: boolean; reason?: string }> => {
    const res = await coreHttp.post<{ row?: string; blocked?: boolean; reason?: string }>(
      `/tickets/${uuid}/form-row`, {});
    return { row: res.data.row, blocked: res.data.blocked === true, reason: res.data.reason };
  },

  undoCopyApprove: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}/copy-approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig') };
    }
  },

  // CS-rep edit-before-approve (#5). Status must be suggestion_ready.
  // scaffoldSegments is additive (Part A (WIM two-drafts, 2026-08) Task 7): omitted for K-path/free_text (no
  // scaffold), so the PATCH body stays byte-identical to pre-Task-7 — present ⇒ the server
  // rewrites scaffold_json's segments node, so a confirmed Klärfall fill survives a reload.
  // suggestionId (fix-review round 1) rides ALONGSIDE scaffoldSegments, never alone — it is the
  // draft's own identity (ai_suggestions.id, the receiver's draftKey) so the server merges into
  // the EXACT row the fill was confirmed against, not whichever row is newest at commit time
  // (a stale client / a reanalysis race). The core handler 400s if scaffoldSegments arrives
  // without it — never falls back to "latest row" — so leaving it out here is not a safe omission.
  editSuggestion: async (
    uuid: string,
    editedText: string,
    scaffoldSegments?: ScaffoldSegment[],
    suggestionId?: number,
  ): Promise<EditSuggestionResult> => {
    try {
      await coreHttp.patch(`/tickets/${uuid}/suggestion-edit`, {
        editedText,
        ...(scaffoldSegments ? { scaffoldSegments, suggestionId } : {}),
      });
      return { success: true };
    } catch (error: any) {
      // Only the scaffoldSegments branch can answer 409 (the server merges into the exact row this
      // fill was confirmed against, and a 0-row merge is the lost race) — but the flag is set from
      // the ANSWER, not from which arguments went out, so a future 409 on the plain text path is
      // read correctly the day it exists rather than silently as a generic conflict.
      return {
        success: false,
        error: formatBackendError(error, 'Speichern der Bearbeitung'),
        ...(isStaleSuggestionError(error) ? { stale: true } : {}),
      };
    }
  },

  // Demo-only (allowlist-gated → 404 elsewhere): resolve deterministic simulated system values for
  // the ticket's empty scaffold slots. Read-only on the server; the FE draft applies + persists them
  // (single persist path). Returns per-slot {slotId,value} for useKlaerfallDraft.fillSystemSlots.
  simulateSystemData: async (uuid: string): Promise<{ success: boolean; filledSlots?: { slotId: string; value: string }[]; error?: string }> => {
    try {
      const res = await coreHttp.post(`/tickets/${uuid}/simulate-system-data`, {});
      return { success: true, filledSlots: res.data?.filledSlots ?? [] };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Simulierte Systemdaten') };
    }
  },

  requestReanalysis: async (uuid: string, body: ReanalysisRequest, options?: { signal?: AbortSignal }): Promise<{ success: boolean; error?: string }> => {
    try {
      // Phase 4B expand window: dual-send both the old and new axis-vocabulary body key with the
      // SAME value — an old (pre-expansion) BE reads suggestedCategory/forceCategory, an expanded
      // one can assert the two agree. Canonical source is the new-named field (ReanalysisRequest);
      // the old key is computed FROM it, never the other way — same direction as the query-param senders above.
      const wireBody: ReanalysisRequest & Record<string, unknown> = { ...body };
      if (body.suggestedPrimaryAxis !== undefined) wireBody.suggestedCategory = body.suggestedPrimaryAxis;
      if (body.forcePrimaryAxis !== undefined) wireBody.forceCategory = body.forcePrimaryAxis;
      await coreHttp.post(`/tickets/${uuid}/request-reanalysis`, wireBody, { signal: options?.signal });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Ablehnen') };
    }
  },

  unblockReanalysis: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/unblock-reanalysis`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Entsperren') };
    }
  },

  archiveTicket: async (uuid: string): Promise<{ success: boolean; previousStatus?: string; error?: string }> => {
    try {
      const res = await coreHttp.post(`/tickets/${uuid}/archive`);
      return { success: true, previousStatus: res.data?.previousStatus };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Archivieren') };
    }
  },

  unarchiveTicket: async (uuid: string, restoreStatus?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/unarchive`, restoreStatus ? { restoreStatus } : {});
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Wiederherstellen') };
    }
  },

  // Auto-button bulk lifecycle (Plan #2). BE returns the count actually changed —
  // already-archived rows in the input set are silent no-ops on BulkArchive.
  // Optional AbortSignal: aborting after the HTTP request reaches BE may still let
  // the server complete the transaction — the FE just discards the response and the
  // 30s RueckgaengigToast continues to provide undo. No client-state corruption.
  // Why a sibling of bulkArchive rather than a new client: it goes through coreHttp, so it inherits the
  // request interceptor's Bearer token with no auth code of its own. A call site that carried its own
  // auth would be one the next token change could silently miss.
  // responseType 'blob' is load-bearing — without it axios coerces the workbook bytes into a JS string
  // and the downloaded file is corrupt in a way that only shows when Excel opens it.
  // Two gestures, one endpoint. `uuids` = "these, which I picked". `selection` = "everything in the
  // view I am looking at" — the server resolves that from the bucket and filters, because the client
  // can only ever enumerate the page it happens to hold.
  downloadFormRowsXlsx: async (
    target:
      | { uuids: string[] }
      // part 7, 2026-10-11 (controller ruling): the selection mirrors EVERY list filter — the
      // attribute and organisation filters and the pill filters — so a workbook never holds rows
      // the filtered screen does not. In the BODY the multi-value members are JSON arrays (core's
      // FormRowsSelection lists), not the query string's comma-joined spelling.
      | { selection: PillFilterParams & {
                       bucket: string; sort?: string; dir?: string; search?: string;
                       primaryAxis?: string; secondaryAxis?: string[];
                       sparte?: string[]; senderKind?: string[]; senderOrganisationId?: string[];
                       from?: string | null; to?: string | null; uploadedBy?: string } },
    options?: { signal?: AbortSignal }
  ): Promise<{ blob?: Blob; includedUuids: string[]; matched: number; delivered: number; fileName?: string; error?: string }> => {
    try {
      // Phase 4B expand window: the SELECTION gesture's filter fields dual-send both the old and
      // new axis-vocabulary key with the SAME value, same as the query-param senders above. The
      // uuids gesture carries no axis filter and needs no transform.
      const wireTarget = 'selection' in target
        ? {
            selection: {
              ...target.selection,
              ...(target.selection.primaryAxis !== undefined ? { category: target.selection.primaryAxis } : {}),
              ...(target.selection.secondaryAxis !== undefined ? { reasonCodes: target.selection.secondaryAxis } : {}),
            },
          }
        : target;
      const res = await coreHttp.post('/tickets/form-rows-xlsx', wireTarget, {
        responseType: 'blob',
        signal: options?.signal,
      });
      // ⚠ MATCHED and DELIVERED are different numbers on purpose. The server selects on "an extraction
      // is stored"; the workbook additionally needs that extraction to be readable. A gap means rows
      // were selected and could not be rendered — the only detector the clerk has, and it also catches
      // the view having moved between the count they saw and the click they made.
      const matched = Number(res.headers['x-smarttix-rows-matched'] ?? 0);
      const delivered = Number(res.headers['x-smarttix-rows-delivered'] ?? 0);
      // Only the picked-uuid gesture names WHICH tickets survived; the selection gesture enumerated
      // nothing client-side, so there is no list to return and none is expected.
      const included = String(res.headers['x-smarttix-included'] || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      // The visible download name is rendered HERE because an anchor's download attribute always wins
      // over Content-Disposition for a blob URL. The server composes the same convention for callers
      // that hit the endpoint directly. Named from DELIVERED rows — the selection gesture has no
      // includedUuids to count.
      // Prefer the delivered count; fall back to the included list when that header is absent. The
      // fallback is not cosmetic: the selection gesture has no included list, and the uuid gesture
      // still names the file correctly against a backend that predates these headers — or when a
      // proxy strips them. Naming a workbook "0-Zeilen" while it holds rows is the same
      // authoritative-but-wrong artifact this whole change removes.
      return { blob: res.data as Blob, includedUuids: included, matched, delivered,
               fileName: notizblattFileName(delivered || included.length) };
    } catch (error: any) {
      return { includedUuids: [], matched: 0, delivered: 0, error: formatBackendError(error, 'Excel-Download') };
    }
  },

  bulkArchive: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ archived: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ archived: number }>('/tickets/bulk-archive', { uuids }, { signal: options?.signal });
      return { archived: res.data.archived };
    } catch (error: any) {
      return { archived: 0, error: formatBackendError(error, 'Archivieren') };
    }
  },

  bulkUnarchive: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ unarchived: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ unarchived: number }>('/tickets/bulk-unarchive', { uuids }, { signal: options?.signal });
      return { unarchived: res.data.unarchived };
    } catch (error: any) {
      return { unarchived: 0, error: formatBackendError(error, 'Wiederherstellen') };
    }
  },

  bulkProcess: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ enqueued: number; skipped: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ enqueued: number; skipped: number }>('/tickets/bulk-process', { uuids }, { signal: options?.signal });
      return { enqueued: res.data.enqueued, skipped: res.data.skipped };
    } catch (error: any) {
      return { enqueued: 0, skipped: 0, error: formatBackendError(error, 'Verarbeiten') };
    }
  },

  // The Liegengeblieben → Erledigt shortcut (Plan #2) lived here — it kept status='archived' and
  // flipped previous_status to 'approved' so the row moved to the Erledigt bucket. The capability
  // is DEACTIVATED for all roles (operator ruling, walk fix round 5, 2026-09-09) and core retires
  // /tickets/bulk-mark-erledigt in parallel, so the client is deleted rather than left standing:
  // an unused client for a route that no longer answers is a 404 with a nice German error message
  // attached, waiting for someone to find a caller for it. The archive does not approve — the route
  // is Wiederherstellen and then the ordinary approval in Offen.

  forceOcr: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/force-ocr`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'OCR-Erzwingen') };
    }
  },

  downloadDraft: async (uuid: string): Promise<void> => {
    const response = await coreHttp.get(`/tickets/${uuid}/download-draft`, {
      responseType: 'blob',
    });
    downloadBlob(new Blob([response.data]), `response-${uuid}.eml`);
  },

  // PII ignore list — UI-only suppression of detected PII entries
  getPiiIgnoreList: async (): Promise<PiiIgnoreEntry[]> => {
    try {
      const response = await coreHttp.get<any[]>(`/pii-ignore`);
      return response.data.map((r: any) => ({
        id: r.id ?? r.Id,
        fieldType: r.fieldType ?? r.FieldType ?? r.field_type,
        value: r.value ?? r.Value,
      }));
    } catch {
      return [];
    }
  },

  addPiiIgnore: async (fieldType: string, value: string): Promise<{ id: number } | null> => {
    try {
      const response = await coreHttp.post('/pii-ignore', { fieldType, value });
      return { id: response.data?.id ?? response.data?.Id };
    } catch {
      return null;
    }
  },

  removePiiIgnore: async (id: number): Promise<boolean> => {
    try {
      await coreHttp.delete(`/pii-ignore/${id}`);
      return true;
    } catch {
      return false;
    }
  },

  getTicketReads: async (): Promise<{ readUuids: string[] }> => {
    const response = await coreHttp.get<{ readUuids: string[] }>(`/tickets/reads`);
    return response.data;
  },

  markTicketRead: async (uuid: string): Promise<void> => {
    await coreHttp.post(`/tickets/read`, { uuid });
  },
};

// ─── Admin API ──────────────────────────────────────────

export const listUsers = async (): Promise<UserEntry[]> => {
  const res = await coreHttp.get<UserEntry[]>(`/manage/users`);
  return res.data;
};

export const addUser = async (email: string, isNormalUser: boolean, isCustomerAdmin: boolean): Promise<void> => {
  await coreHttp.post('/manage/users', { email, isNormalUser, isCustomerAdmin });
};

export const deleteUser = async (id: number): Promise<void> => {
  await coreHttp.delete(`/manage/users/${id}`);
};

export const updateUserRoles = async (id: number, isNormalUser: boolean, isCustomerAdmin: boolean): Promise<void> => {
  await coreHttp.put(`/manage/users/${id}/roles`, { isNormalUser, isCustomerAdmin });
};

export const getCustomerConfig = async (): Promise<Record<string, string>> => {
  const res = await coreHttp.get<Record<string, string>>(`/manage/config`);
  return res.data;
};

export const updateCustomerConfig = async (entries: Record<string, string>): Promise<void> => {
  await coreHttp.put(`/manage/config`, { entries });
};

export const listAnswerCatalog = async (): Promise<AnswerCapabilityEntry[]> => {
  const res = await coreHttp.get<AnswerCapabilityEntry[]>(`/manage/answer-catalog`);
  return res.data;
};

export const listReplyPrompts = async (): Promise<ReplyPromptEntry[]> => {
  const res = await coreHttp.get<ReplyPromptEntry[]>(`/manage/reply-prompts`);
  return res.data;
};
export const saveReplyPrompt = async (component: string, text: string, baseRevision: number): Promise<void> => {
  await coreHttp.post(`/manage/reply-prompts`, { component, text, baseRevision });
};
export const rollbackReplyPrompt = async (component: string, revision: number): Promise<void> => {
  await coreHttp.post(`/manage/reply-prompts/${encodeURIComponent(component)}/rollback`, { revision });
};
export const resetReplyPrompt = async (component: string): Promise<void> => {
  await coreHttp.delete(`/manage/reply-prompts/${encodeURIComponent(component)}`);
};

// Gen-8 tolerance (final review finding 3): a backend on the generation this endpoint predates
// serves no `axes` at all (on some fixtures) — normalized here, once, rather than at every caller,
// so `OverlayRows.flattenAxes` never has to guard against `undefined` itself. An honest empty
// list, not a crash, on an old backend. A backend older than this pass (part 4c, R36.6) may still
// send a `families` field in the wire payload — it is simply not read, no type error at runtime.
export const listVocabulary = async (): Promise<VocabularyCatalogResponse> => {
  const res = await coreHttp.get<VocabularyCatalogResponse>(`/manage/vocabulary`);
  return { ...res.data, axes: res.data.axes ?? [] };
};
// 409 (stale baseRevision) propagates to the caller exactly as saveReplyPrompt's does — the
// lost-update recovery is a reload, and only the editor knows how to preserve the rejected draft.
// `abbr` (part 4b): undefined omits the key entirely (a non-primary-axis save sends none — only
// the primary axis carries a letters band), `null` sends an explicit clear (the admin emptied the
// letters field), a string sends the current letters draft. Distinguishing undefined from null
// here is deliberate — see OverlayRows.tsx's save().
export const saveVocabularyLabel = async (
  axis: string, valueId: string, labelDe: string, baseRevision: number, abbr?: string | null,
): Promise<void> => {
  await coreHttp.post(`/manage/vocabulary`,
    { axis, valueId, labelDe, baseRevision, ...(abbr === undefined ? {} : { abbr }) });
};
export const resetVocabularyLabel = async (axis: string, valueId: string): Promise<void> => {
  await coreHttp.delete(`/manage/vocabulary/${encodeURIComponent(axis)}/${encodeURIComponent(valueId)}`);
};

export const upsertAct = async (act: VnbActUpsert): Promise<void> => {
  await coreHttp.post(`/manage/acts`, act);
};
export const deleteAct = async (actId: string): Promise<void> => {
  await coreHttp.delete(`/manage/acts/${encodeURIComponent(actId)}`);
};

export const listActs = async (): Promise<VnbAct[]> => {
  const res = await coreHttp.get<VnbAct[]>(`/manage/acts`);
  return res.data;
};

export const listRoutingRules = async (): Promise<RoutingRule[]> => {
  const res = await coreHttp.get<RoutingRule[]>(`/manage/routing`);
  return res.data;
};
export const upsertRoutingRule = async (rule: RoutingRuleUpsert): Promise<void> => {
  await coreHttp.post(`/manage/routing`, rule);
};
export const deleteRoutingRule = async (ruleId: string): Promise<void> => {
  await coreHttp.delete(`/manage/routing/${encodeURIComponent(ruleId)}`);
};

export const listSenderOrganisations = async (): Promise<SenderOrganisationRow[]> => {
  const res = await coreHttp.get<SenderOrganisationRow[]>(`/manage/senders`);
  return res.data;
};
export const createSenderOrganisation = async (payload: SenderOrganisationUpsert): Promise<SenderOrganisationRow> => {
  const res = await coreHttp.post<SenderOrganisationRow>(`/manage/senders`, payload);
  return res.data;
};
export const updateSenderOrganisation = async (id: string, payload: SenderOrganisationUpsert): Promise<SenderOrganisationRow> => {
  const res = await coreHttp.patch<SenderOrganisationRow>(`/manage/senders/${encodeURIComponent(id)}`, payload);
  return res.data;
};
export const confirmSenderOrganisation = async (id: string): Promise<void> => {
  await coreHttp.post(`/manage/senders/${encodeURIComponent(id)}/confirm`);
};
export const rejectSenderOrganisation = async (id: string): Promise<void> => {
  await coreHttp.post(`/manage/senders/${encodeURIComponent(id)}/reject`);
};
export const mergeSenderOrganisation = async (id: string, into: string): Promise<SenderOrganisationMergeResult> => {
  const res = await coreHttp.post<SenderOrganisationMergeResult>(`/manage/senders/${encodeURIComponent(id)}/merge`, { into });
  return res.data;
};
export const deleteSenderOrganisation = async (id: string): Promise<void> => {
  await coreHttp.delete(`/manage/senders/${encodeURIComponent(id)}`);
};

// The one endpoint here whose failure is SWALLOWED, and deliberately: every backend on the fleet
// answers 404 until Task C6 deploys, and "no stored preference" is the honest reading of a read
// that did not land — the caller then uses the tenant default, which is what it would have done
// anyway. Its sibling PUT below takes the opposite posture on purpose.
export const getMyUiPreferences = async (): Promise<UiPreferences> => {
  try {
    const res = await coreHttp.get<UiPreferences>('/me/ui-preferences');
    // NORMALISED HERE, the same posture getConfig takes one screen up: a shape this client cannot
    // read is not a state the rest of the app should have to recognise. A 200 whose body carries no
    // `rowPills` at all (a backend that serves the envelope before it serves this preference) and a
    // `hidden` that is not a list both mean the one thing the caller can act on — NO STORED
    // PREFERENCE, i.e. use the tenant default. Anything else would hand `hidden.includes(...)` a
    // value it cannot answer for, one layer further in and with no way back to the wire.
    const stored = res.data.rowPills;
    if (!stored || !Array.isArray(stored.hidden)) return { rowPills: null };
    return { rowPills: { hidden: stored.hidden } };
  } catch {
    return { rowPills: null };
  }
};
// NO catch: a failed WRITE must reach the settings page, which has a feedback slot to say so in.
// Swallowing it would paint a save that never happened.
export const putMyRowPillPreferences = async (hidden: string[]): Promise<RowPillPreferences> => {
  const res = await coreHttp.put<RowPillPreferences>('/me/ui-preferences/row-pills', { hidden });
  return res.data;
};
export const getRowPillSurvey = async (): Promise<RowPillSurvey> => {
  const res = await coreHttp.get<RowPillSurvey>('/manage/ui-preferences/row-pills/survey');
  return res.data;
};

// ─── Test Generator API ────────────────────────────────

export const generateTestEmail = async (req: GenerateTestEmailRequest): Promise<GeneratedEmail> => {
  const res = await coreHttp.post<GeneratedEmail>('/tools/generate-test-email', req);
  return res.data;
};

// role is required — no default. A defaulted role here was the same silent-gmsb shape as the
// App.tsx/getConfig sites; the caller (TestgeneratorSeite) already holds the live confirmed role
// and must pass it explicitly.
export const listSignalAtoms = async (role: string): Promise<SignalAtomEntry[]> => {
  const res = await coreHttp.get<SignalAtomEntry[]>(`/tools/signal-atoms?role=${encodeURIComponent(role)}`);
  return res.data;
};

// Why: atoms are read-only for admin users. Managed via seed_signal_atoms.py → template → --db-only sync.

const emlSafeName = (subject: string): string =>
  subject.replace(/[^a-zA-Z0-9äöüÄÖÜß _-]/g, '').slice(0, 50) || 'test-email';

export const emlBase64ToFile = (emlBase64: string, subject: string): File => {
  const bytes = Uint8Array.from(atob(emlBase64), c => c.charCodeAt(0));
  return new File([bytes], `${emlSafeName(subject)}.eml`, { type: 'message/rfc822' });
};

export const downloadTestEmail = (emlBase64: string, subject: string): void => {
  const bytes = Uint8Array.from(atob(emlBase64), c => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'message/rfc822' });
  downloadBlob(blob, `${emlSafeName(subject)}.eml`);
};
